import React, { useEffect, useState } from 'react';
import Layout from '../Layout/Layout.jsx';
import styles from '../Common/Common.module.css';
import myVideosStyles from './MyVideosStep.module.css';
import { testimonyApi } from '../../services/api.js';

export default function MyVideosStep({ userToken, onBack }) {
  const [testimonies, setTestimonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingKey, setDownloadingKey] = useState(null); // `${testimonyId}-${stage}`
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    loadMyTestimonies();
  }, []);

  const loadMyTestimonies = async () => {
    try {
      setLoading(true);
      const result = await testimonyApi.getMyTestimonies(userToken);
      setTestimonies(result.data?.testimonies || []);
    } catch (err) {
      setError(err.message || 'Failed to load your submissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (testimonyId, stage) => {
    const key = `${testimonyId}-${stage}`;
    setDownloadingKey(key);
    setDownloadError('');
    try {
      const data = await testimonyApi.getMyDownloadUrl(testimonyId, stage, userToken);
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setDownloadError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloadingKey(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={myVideosStyles.card}>
          {/* Header */}
          <div className={myVideosStyles.header}>
            <button onClick={onBack} className={myVideosStyles['back-btn']}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back
            </button>
            <h1 className={myVideosStyles.title}>My Submissions</h1>
            <p className={myVideosStyles.subtitle}>View and download your submitted video testimonies</p>
          </div>

          {/* Content */}
          <div className={myVideosStyles.content}>
            {loading && (
              <div className={myVideosStyles.loading}>
                <div className={myVideosStyles.spinner}></div>
                <p>Loading your submissions...</p>
              </div>
            )}

            {error && !loading && (
              <div className={myVideosStyles['error-box']}>
                <p>{error}</p>
              </div>
            )}

            {downloadError && (
              <div className={myVideosStyles['error-box']} style={{ marginBottom: 16 }}>
                <p>{downloadError}</p>
              </div>
            )}

            {!loading && !error && testimonies.length === 0 && (
              <div className={myVideosStyles.empty}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                <p>No submissions found.</p>
              </div>
            )}

            {!loading && testimonies.map((testimony) => {
              const hasStages = testimony.videos && testimony.videos.length > 0;
              const stages = hasStages ? testimony.videos.filter(v => v.videoUrl) : [];

              return (
                <div key={testimony._id} className={myVideosStyles['testimony-card']}>
                  {/* Testimony header */}
                  <div className={myVideosStyles['testimony-header']}>
                    <div>
                      <h3 className={myVideosStyles['testimony-name']}>
                        {testimony.firstName} {testimony.lastName}
                      </h3>
                      <p className={myVideosStyles['testimony-meta']}>
                        Submitted {formatDate(testimony.createdAt)}
                        {stages.length > 0 && ` · ${stages.length} stage${stages.length > 1 ? 's' : ''}`}
                      </p>
                    </div>
                    <span className={`${myVideosStyles.badge} ${myVideosStyles[`badge-${testimony.status}`] || myVideosStyles['badge-pending']}`}>
                      {testimony.status}
                    </span>
                  </div>

                  {/* Stage videos */}
                  {stages.length > 0 && (
                    <div className={myVideosStyles['stages-list']}>
                      {stages.map((stage) => {
                        const key = `${testimony._id}-${stage.stage}`;
                        const isDownloading = downloadingKey === key;
                        return (
                          <div key={stage.stage} className={myVideosStyles['stage-row']}>
                            <div className={myVideosStyles['stage-info']}>
                              <div className={myVideosStyles['stage-number']}>{stage.stage}</div>
                              <div>
                                <p className={myVideosStyles['stage-title']}>{stage.stageTitle}</p>
                                {stage.duration && (
                                  <p className={myVideosStyles['stage-duration']}>{Math.round(stage.duration)}s</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownload(testimony._id, stage.stage)}
                              disabled={!!downloadingKey}
                              className={myVideosStyles['download-btn']}
                            >
                              {isDownloading ? (
                                <span className={myVideosStyles['btn-spinner']}></span>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                  <polyline points="7,10 12,15 17,10"/>
                                  <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                              )}
                              {isDownloading ? 'Preparing...' : 'Download'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Legacy single video */}
                  {!hasStages && testimony.videoUrl && (
                    <div className={myVideosStyles['stages-list']}>
                      <div className={myVideosStyles['stage-row']}>
                        <div className={myVideosStyles['stage-info']}>
                          <div className={myVideosStyles['stage-number']}>1</div>
                          <div>
                            <p className={myVideosStyles['stage-title']}>Video Testimony</p>
                            {testimony.duration && (
                              <p className={myVideosStyles['stage-duration']}>{Math.round(testimony.duration)}s</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(testimony._id, null)}
                          disabled={!!downloadingKey}
                          className={myVideosStyles['download-btn']}
                        >
                          {downloadingKey === `${testimony._id}-null` ? (
                            <span className={myVideosStyles['btn-spinner']}></span>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7,10 12,15 17,10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                          )}
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
